import { Link } from "react-router-dom";
import { Award, Calendar, ShieldCheck } from "lucide-react";
import { founder, brand } from "@/data/site/brand";

interface AuthorBioProps {
  /** Display name. Defaults to the firm's founder. */
  name?: string;
  credentials?: string;
  title?: string;
  bio?: string;
  profileUrl?: string;
  /** Optional reviewer line. */
  reviewedBy?: string;
  /** Human-readable last-updated date. */
  lastUpdated?: string;
}

/**
 * E-E-A-T author block — surfaces credentials, firm affiliation, and an
 * optional reviewer line at the end of long-form content.
 */
export const AuthorBio = ({
  name = founder.name,
  credentials = founder.credentials,
  title = founder.title,
  bio,
  profileUrl = "/about/franklin-sofi",
  reviewedBy,
  lastUpdated,
  image = founder.image,
}: AuthorBioProps & { image?: string }) => {
  const blurb =
    bio ??
    `${name} is a ${credentials} and the founder of ${brand.legalName}. ${founder.bio.split(".").slice(0, 2).join(".")}.`;

  return (
    <div className="not-prose mt-16 bg-secondary/40 border border-border rounded-xl p-6 md:p-8">
      <div className="flex items-start gap-4 mb-4">
        <img
          src={image}
          alt={`${name}, ${credentials}`}
          className="w-16 h-16 rounded-full object-cover object-top border-2 border-gold shrink-0"
          loading="lazy"
         width={80} height={80} />
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">
            Written by
          </div>
          <Link
            to={profileUrl}
            className="text-lg font-heading font-bold text-navy hover:text-gold transition-colors"
          >
            {name}
          </Link>
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
      </div>

      <p className="text-sm text-foreground/85 leading-relaxed mb-4">{blurb}</p>

      <div className="grid sm:grid-cols-2 gap-4 text-xs text-muted-foreground mb-4">
        <div className="flex gap-2">
          <ShieldCheck className="w-4 h-4 text-navy shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-navy mb-0.5">Credentials</div>
            <div>{credentials}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Award className="w-4 h-4 text-navy shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-navy mb-0.5">Memberships</div>
            <div>ASTPS Gold Member · AICPA · TXCPA</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground pt-4 border-t border-border">
        {reviewedBy && (
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>
              Reviewed by <span className="font-semibold text-navy">{reviewedBy}</span>
            </span>
          </div>
        )}
        {lastUpdated && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-navy" />
            <span>Last updated {lastUpdated}</span>
          </div>
        )}
        <Link
          to={profileUrl}
          className="ml-auto text-navy font-semibold hover:text-gold transition-colors"
        >
          Read full bio →
        </Link>
      </div>
    </div>
  );
};
